import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ChevronRight } from "lucide-react";
import { AREAS, SERVICES } from "@/data/site";
import { usePageSeo } from "@/lib/seo";
import { CallButton, QuoteButton } from "@/components/site/Cta";
import { CaseStudies, CertStrip, Faqs, QuoteSection, SectionHead, ServicesGrid } from "@/components/site/Sections";
import NotFound from "./NotFound";

export function Crumbs({ items }: { items: { to?: string; label: string }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-1 font-mono text-xs text-slate-500">
      {items.map((c, i) => (
        <span key={c.label} className="flex items-center gap-1">
          {c.to ? <Link to={c.to} className="hover:text-brand-400">{c.label}</Link> : <span className="text-slate-300">{c.label}</span>}
          {i < items.length - 1 && <ChevronRight className="h-3 w-3" />}
        </span>
      ))}
    </nav>
  );
}

export default function ServicePage() {
  const { slug } = useParams();
  const s = SERVICES.find((x) => x.slug === slug);
  usePageSeo(`/services/${slug}`);
  if (!s) return <NotFound />;
  return (
    <>
      <section className="relative overflow-hidden border-b border-slate-800" data-testid={`service-page-${s.slug}`}>
        <img src={s.image} alt={s.h1} className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F1115] via-[#0F1115]/90 to-[#0F1115]/40" />
        <div className="relative mx-auto max-w-7xl px-5 py-16 md:px-8 lg:py-24">
          <Crumbs items={[{ to: "/", label: "Home" }, { to: "/#services", label: "Services" }, { label: s.title }]} />
          <h1 className="reveal max-w-4xl text-4xl font-extrabold uppercase leading-[0.95] text-white sm:text-5xl lg:text-6xl">{s.h1}</h1>
          <p className="mt-6 max-w-2xl text-base text-slate-300 md:text-lg">{s.intro}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <CallButton id="service-phone-cta" />
            <QuoteButton id="service-quote-cta" />
          </div>
          <div className="mt-12 grid max-w-4xl grid-cols-2 gap-px bg-slate-700/60 md:grid-cols-4">
            {s.specs.map((sp) => (
              <div key={sp.label} className="bg-[#0F1115]/90 p-4">
                <div className="font-mono text-[11px] uppercase text-slate-500">{sp.label}</div>
                <div className="mt-1 font-display text-xl font-bold text-brand-500">{sp.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CertStrip />
      <section className="mx-auto grid max-w-7xl gap-16 px-5 py-20 md:px-8 lg:grid-cols-3">
        <div>
          <h2 className="eyebrow mb-4">Applications</h2>
          <ul className="space-y-3">{s.applications.map((a) => <li key={a} className="border-b border-slate-800 pb-3 text-lg text-white">{a}</li>)}</ul>
        </div>
        <div>
          <h2 className="eyebrow mb-4">Why specify us</h2>
          <ul className="space-y-3">{s.benefits.map((b) => <li key={b} className="flex gap-3 text-slate-300"><CheckCircle2 className="h-5 w-5 shrink-0 text-brand-500" />{b}</li>)}</ul>
        </div>
        <div>
          <h2 className="eyebrow mb-4">Our process</h2>
          <ol className="space-y-3">{s.process.map((p, i) => <li key={p} className="flex gap-3 text-slate-300"><span className="font-mono text-brand-500">0{i + 1}</span>{p}</li>)}</ol>
        </div>
      </section>
      <section className="bg-[#161920]">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8">
          <SectionHead eyebrow="Recent projects" title={`${s.title} projects`} />
          <CaseStudies />
        </div>
      </section>
      <section className="mx-auto grid max-w-7xl gap-12 px-5 py-20 md:px-8 lg:grid-cols-[1fr_1.5fr]">
        <div>
          <SectionHead eyebrow="Service areas" title="Local crews" />
          <ul className="space-y-2">
            {AREAS.map((a) => (
              <li key={a.slug}><Link to={`/areas/${a.slug}`} data-testid={`service-area-link-${a.slug}`} className="text-slate-300 underline-offset-4 hover:text-brand-400 hover:underline">{s.title} {a.name}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <SectionHead eyebrow="FAQ" title="Common questions" />
          <Faqs items={s.faqs} />
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-5 pb-20 md:px-8">
        <SectionHead eyebrow="Related systems" title="Other services" />
        <ServicesGrid exclude={s.slug} />
      </section>
      <QuoteSection title={`Quote: ${s.title}`} workType={s.workType} />
    </>
  );
}
